import type { RolePermissionRole } from '@/store/modules/rolePermission';

/** All level-2 managers that list this staff role in `managedChildRoleIds`. */
export function findParentRoleIdsForStaff(
  staffRoleId: number,
  roles: RolePermissionRole[]
): number[] {
  return roles
    .filter(
      (role) =>
        role.level === 2 &&
        (role.managedChildRoleIds ?? []).includes(staffRoleId)
    )
    .map((role) => role.id);
}

/** Options shown when assigning a parent to 担当者 (スーパー管理者 + level-2 managers). */
export function buildStaffParentRoleOptions(
  roles: RolePermissionRole[]
): RolePermissionRole[] {
  const superAdmin = roles.find((role) => role.level === 3);
  const managers = roles.filter((role) => role.level === 2);
  return [...(superAdmin ? [superAdmin] : []), ...managers];
}

export function findLevel3RoleId(
  roles: RolePermissionRole[]
): number | undefined {
  return roles.find((role) => role.level === 3)?.id;
}

/** Default 親ロール checkboxes: level 3 only. */
export function defaultParentRoleCheckboxIds(
  roles: RolePermissionRole[]
): number[] {
  const level3Id = findLevel3RoleId(roles);
  return level3Id ? [level3Id] : [];
}

/** Keep level 3 checked; allow multiple selections. */
export function normalizeParentRoleCheckboxIds(
  values: number[],
  options: RolePermissionRole[]
): number[] {
  const level3Id = findLevel3RoleId(options);
  const unique = [...new Set(values)];
  if (level3Id && !unique.includes(level3Id)) {
    return [level3Id, ...unique];
  }
  return unique;
}

export function buildParentRoleCheckboxIdsFromDetail(
  detail: { parentRoleIds?: number[] },
  staffRoleId: number,
  roles: RolePermissionRole[]
): number[] {
  const ids = new Set<number>();
  const level3Id = findLevel3RoleId(roles);
  if (level3Id) ids.add(level3Id);

  detail.parentRoleIds?.forEach((id) => ids.add(id));
  findParentRoleIdsForStaff(staffRoleId, roles).forEach((id) => ids.add(id));

  return [...ids];
}

/** Resolved parent role ids for readonly detail (API field + managedChildRoleIds inference). */
export function buildParentRoleIdsForDisplay(
  detail: {
    id: number;
    level: 1 | 2 | 3;
    parentRoleIds?: number[];
  },
  roles: RolePermissionRole[]
): number[] {
  if (detail.level === 1) {
    return buildParentRoleCheckboxIdsFromDetail(detail, detail.id, roles);
  }
  if (detail.level === 2) {
    const level3Id = findLevel3RoleId(roles);
    return level3Id ? [level3Id] : [];
  }
  return [];
}

/** Payload `parentRoleIds`: level 1 from checkboxes; level 2 auto includes level 3. */
export function buildParentRoleIdsForSave(
  level: 1 | 2 | 3,
  checkboxIds: number[],
  roles: RolePermissionRole[]
): number[] {
  if (level === 1) {
    return normalizeParentRoleCheckboxIds(checkboxIds, roles);
  }
  if (level === 2) {
    const level3Id = findLevel3RoleId(roles);
    return level3Id ? [level3Id] : [];
  }
  return [];
}
